import type { ICountry } from 'countries-states-cities';
import { z } from 'zod';
import { allCountries } from '~/utils/address';

export type PhoneCountry = {
  key: string;
  iso2: string;
  name: string;
  flag: string;
  dialCode: string;
};

export type PhoneNumberParts = {
  countryKey: string;
  nationalNumber: string;
  extension: string;
};

export const MAX_PHONE_EXTENSION_LENGTH = 6;

const E164_PHONE_NUMBER_WITH_EXTENSION = /^\+[1-9]\d{6,14}(;ext=\d{1,6})?$/;

const MAX_RAW_PHONE_NUMBER_LENGTH = 40;

const EXTENSION_MARKERS = ['extension', 'ext.', 'ext', 'x'];

export const optionalPhoneNumberSchema = z
  .string()
  .trim()
  .refine(
    (phoneNumber) =>
      phoneNumber === '' || E164_PHONE_NUMBER_WITH_EXTENSION.test(phoneNumber)
  )
  .transform((phoneNumber) => (phoneNumber === '' ? null : phoneNumber));

export const isValidPhoneNumber = (phoneNumber: string) =>
  optionalPhoneNumberSchema.safeParse(phoneNumber).success;

const FALLBACK_COUNTRY_ISO2 = 'CA';

// The dataset writes phone codes as "93", "+1-268" or "+1-809 and 1-829".
function dialCodesOf(phoneCode: string): string[] {
  return phoneCode
    .split(' and ')
    .map(digitsOf)
    .filter((code) => code.length > 0);
}

export const PHONE_COUNTRIES: PhoneCountry[] = allCountries.flatMap((country) =>
  dialCodesOf(country.phone_code).map((dialCode) => ({
    key: `${country.iso2}:${dialCode}`,
    iso2: country.iso2,
    name: country.name,
    flag: country.emoji,
    dialCode,
  }))
);

const phoneCountryByKey = new Map(
  PHONE_COUNTRIES.map((phoneCountry) => [phoneCountry.key, phoneCountry])
);

const phoneCountryIso2s = new Set(
  PHONE_COUNTRIES.map((phoneCountry) => phoneCountry.iso2)
);

function regionOfLanguageTag(languageTag: string): string | undefined {
  try {
    return new Intl.Locale(languageTag).maximize().region;
  } catch {
    return undefined;
  }
}

type CountryWithTimeZones = ICountry & {
  timezones?: { zoneName: string }[];
};

const countryIso2ByTimeZone = new Map<string, string>();
for (const country of allCountries as CountryWithTimeZones[]) {
  for (const timeZone of country.timezones ?? []) {
    if (!countryIso2ByTimeZone.has(timeZone.zoneName)) {
      countryIso2ByTimeZone.set(timeZone.zoneName, country.iso2);
    }
  }
}

function deviceTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

function countryIso2OfTimeZone(
  timeZone: string | undefined
): string | undefined {
  if (timeZone === undefined) return undefined;
  const countryIso2 = countryIso2ByTimeZone.get(timeZone);
  return countryIso2 !== undefined && phoneCountryIso2s.has(countryIso2)
    ? countryIso2
    : undefined;
}

function countryIso2OfLanguageTags(
  languageTags: readonly string[]
): string | undefined {
  return languageTags
    .map(regionOfLanguageTag)
    .find(
      (region): region is string =>
        region !== undefined && phoneCountryIso2s.has(region)
    );
}

export function browserCountryIso2(
  timeZone: string | undefined = deviceTimeZone(),
  languageTags: readonly string[] = navigator.languages
): string {
  return (
    countryIso2OfTimeZone(timeZone) ??
    countryIso2OfLanguageTags(languageTags) ??
    FALLBACK_COUNTRY_ISO2
  );
}

const DEFAULT_COUNTRY_ISO2 = browserCountryIso2();

export const DEFAULT_PHONE_COUNTRY = PHONE_COUNTRIES.find(
  (phoneCountry) => phoneCountry.iso2 === DEFAULT_COUNTRY_ISO2
) as PhoneCountry;

export function findPhoneCountry(countryKey: string): PhoneCountry {
  return phoneCountryByKey.get(countryKey) ?? DEFAULT_PHONE_COUNTRY;
}

export function digitsOf(text: string): string {
  return text.replace(/\D/g, '');
}

const isDigit = (character: string) => character >= '0' && character <= '9';

const isWhitespace = (character: string) => character.trim() === '';

function skipWhitespaceBackward(text: string, end: number): number {
  let position = end;
  while (position > 0 && isWhitespace(text[position - 1])) {
    position--;
  }
  return position;
}

function endsWithIgnoreCase(text: string, end: number, suffix: string) {
  const start = end - suffix.length;
  return (
    start >= 0 && text.slice(start, end).toLowerCase() === suffix.toLowerCase()
  );
}

function findExtensionMarkerStart(phoneNumber: string, markerEnd: number) {
  if (markerEnd > 0 && phoneNumber[markerEnd - 1] === '=') {
    const extEnd = skipWhitespaceBackward(phoneNumber, markerEnd - 1);
    if (!endsWithIgnoreCase(phoneNumber, extEnd, 'ext')) return -1;
    const semicolonEnd = skipWhitespaceBackward(
      phoneNumber,
      extEnd - 'ext'.length
    );
    return semicolonEnd > 0 && phoneNumber[semicolonEnd - 1] === ';'
      ? semicolonEnd - 1
      : -1;
  }
  const marker = EXTENSION_MARKERS.find((extensionMarker) =>
    endsWithIgnoreCase(phoneNumber, markerEnd, extensionMarker)
  );
  return marker === undefined ? -1 : markerEnd - marker.length;
}

function splitExtension(phoneNumber: string): {
  number: string;
  extension: string;
} {
  const withoutExtension = { number: phoneNumber, extension: '' };
  if (phoneNumber.length > MAX_RAW_PHONE_NUMBER_LENGTH) return withoutExtension;
  let extensionStart = phoneNumber.length;
  while (extensionStart > 0 && isDigit(phoneNumber[extensionStart - 1])) {
    extensionStart--;
  }
  if (extensionStart === phoneNumber.length) return withoutExtension;
  const markerStart = findExtensionMarkerStart(
    phoneNumber,
    skipWhitespaceBackward(phoneNumber, extensionStart)
  );
  if (markerStart <= 0) return withoutExtension;
  return {
    number: phoneNumber.slice(
      0,
      skipWhitespaceBackward(phoneNumber, markerStart)
    ),
    extension: phoneNumber.slice(extensionStart),
  };
}

function pickSharedDialCodeCountry(
  candidates: PhoneCountry[],
  previousCountryKey: string | undefined
): PhoneCountry {
  return (
    candidates.find((candidate) => candidate.key === previousCountryKey) ??
    candidates.find((candidate) => candidate.iso2 === DEFAULT_COUNTRY_ISO2) ??
    candidates[0]
  );
}

export function parsePhoneNumber(
  phoneNumber: string,
  previousCountryKey?: string
): PhoneNumberParts {
  const fallbackCountryKey = findPhoneCountry(
    previousCountryKey ?? DEFAULT_PHONE_COUNTRY.key
  ).key;
  const { number: trimmedPhoneNumber, extension } = splitExtension(
    phoneNumber.trim()
  );
  const digits = digitsOf(trimmedPhoneNumber);
  if (!trimmedPhoneNumber.startsWith('+') || digits === '') {
    return {
      countryKey: fallbackCountryKey,
      nationalNumber: digits,
      extension,
    };
  }
  const matchingCountries = PHONE_COUNTRIES.filter((phoneCountry) =>
    digits.startsWith(phoneCountry.dialCode)
  );
  if (matchingCountries.length === 0) {
    return {
      countryKey: fallbackCountryKey,
      nationalNumber: digits,
      extension,
    };
  }
  const longestDialCodeLength = Math.max(
    ...matchingCountries.map((phoneCountry) => phoneCountry.dialCode.length)
  );
  const matchedCountry = pickSharedDialCodeCountry(
    matchingCountries.filter(
      (phoneCountry) => phoneCountry.dialCode.length === longestDialCodeLength
    ),
    previousCountryKey
  );
  return {
    countryKey: matchedCountry.key,
    nationalNumber: digits.slice(longestDialCodeLength),
    extension,
  };
}

export function composePhoneNumber({
  countryKey,
  nationalNumber,
  extension,
}: PhoneNumberParts): string {
  const extensionSuffix = extension === '' ? '' : `;ext=${extension}`;
  if (nationalNumber === '') return extensionSuffix;
  return `+${findPhoneCountry(countryKey).dialCode}${nationalNumber}${extensionSuffix}`;
}

const NORTH_AMERICAN_NUMBER = /^1(\d{3})(\d{3})(\d{4})$/;

export function formatPhoneNumber(phoneNumber: string): string {
  const { number, extension } = splitExtension(phoneNumber);
  const formattedNumber = formatNumberWithoutExtension(number);
  return extension === ''
    ? formattedNumber
    : `${formattedNumber} ext. ${extension}`;
}

function formatNumberWithoutExtension(phoneNumber: string): string {
  const digits = digitsOf(phoneNumber);
  if (!phoneNumber.startsWith('+') || digits === '') return phoneNumber;
  const northAmericanParts = NORTH_AMERICAN_NUMBER.exec(digits);
  if (northAmericanParts !== null) {
    return `+1 ${northAmericanParts.slice(1).join(' ')}`;
  }
  const { countryKey, nationalNumber } = parsePhoneNumber(phoneNumber);
  const dialCode = findPhoneCountry(countryKey).dialCode;
  if (`${dialCode}${nationalNumber}` !== digits) return phoneNumber;
  return `+${dialCode} ${nationalNumber}`;
}
